import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Checkbox } from '@contrast-security-inc/design-system-inputs'
import {
  CheckboxListItemType,
  SelectionFilterValue,
  SelectionListItemType
} from '../../../../../types'
import { ExpandableList, ListSearchInput, useListSearch } from '../../../layout'
import { OnInputChangeHandler } from '@contrast-security-inc/design-system-inputs/dist/types/shared'
import { EmptyFilterComponent, FilterProps } from '../../filters'
import { CheckLabel } from '../../check-label'

export type SelectionFilters = Record<string, SelectionFilterValue>

interface FilterSelectionProps extends Omit<FilterProps, 'filters' | 'resetFilter'> {
  filters: SelectionFilters
}
export const FilterSelection = ({
  filterDef: {
    filterMsgId,
    filterKey,
    useFilterList,
    options: { search = false, showAll = false, maxHeight } = {}
  },
  filters,
  updateFilter
}: FilterSelectionProps) => {
  // state/hooks
  const intl = useIntl()
  const { data, isLoading } = useFilterList({ filters })
  const {
    searchText,
    onChangeSearchText,
    searchedList: filtersList
  } = useListSearch({ listItems: data?.data?.filters as CheckboxListItemType[] })
  const searchPlaceholder = intl.formatMessage(
    { id: 'placeholder_search' },
    // ts-ignore dynamic msg id
    { type: intl.formatMessage({ id: filterMsgId }) }
  )
  // events
  const onFilterChange: OnInputChangeHandler<boolean, HTMLInputElement> = React.useCallback(
    (event, checkState) => {
      const startId = event.target.id.indexOf('-') + 1 // one char past the dash
      const id = event.target.id.substring(startId)

      updateFilter(filterKey, { id, flag: checkState })
    },
    [filterKey, updateFilter]
  )
  // this is possibly generic, onFilterChange being the only unique thing
  const SelectionListItem = React.useCallback(
    ({ keycode, label, count }: SelectionListItemType) => {
      return (
        <Checkbox
          key={keycode}
          id={`${filterKey}-${keycode}`}
          testId={`${filterKey}-${keycode}`}
          label={<CheckLabel count={count} value={label} />}
          isDisabled={count === 0}
          isChecked={
            filters[filterKey].indexOf(keycode) !== -1 ||
            // this accounts for inconsistent keycodes used in dashboard api
            filters[filterKey].indexOf(keycode.toUpperCase()) !== -1
          }
          onChange={onFilterChange}
        />
      )
    },
    [filterKey, onFilterChange, filters]
  )

  return (
    <div data-testid="filter-selection">
      {search && (
        <div
          className={css`
            margin-bottom: 16px;
          `}
        >
          <ListSearchInput
            onChangeSearchText={onChangeSearchText}
            searchText={searchText}
            placeholder={searchPlaceholder}
          />
        </div>
      )}
      <div
        className={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <ExpandableList
          maxHeight={maxHeight}
          showAll={showAll}
          isLoading={isLoading}
          listItems={filtersList}
          EmptyListComponent={EmptyFilterComponent}
          // @ts-ignore we know this is correct
          ItemComponent={SelectionListItem}
        />
      </div>
    </div>
  )
}
